/* RunScreenComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./RunScreenRenderer");
const ScheduleVeilComponent = require("../../../../lib/actions/veils/schedule/ScheduleVeilComponent");
const Registrations = require("../../../../../RegistrationNamespaces");
const _ = require("lodash");

class RunScreenComponent extends EventEmitter {
    constructor(
        logger,
        GameEditor,
        OsUpgradePlan,
        DeviceEntity,
        ActiveView,
        modalUserFeedback,
        userSession
    ) {
        super();
        this._logger = logger;
        this._gameEditor = GameEditor;
        this._osPlanEntity = OsUpgradePlan;
        this._activeView = ActiveView;
        this._modalUserFeedback = modalUserFeedback;
        this._userSession = userSession;
        this._renderer = new Renderer(this);
        this._registerListeners();
        this._renderer.render();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    get logger() {
        return this._logger;
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._logger = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "RunScreenComponent.shutdown() error.");
            }
        );
    }

    // prettier-ignore
    set "$teve.ui.context" (context) { //jshint ignore:line
        this._context = context;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    schedulePlan(workInput) {
        let entity = this._gameEditor.getPlan();
        let titleMsg = "Run OS Plan - Schedule";

        return this._checkDirty()
            .then(() => {
                return this._checkPlanValidated();
            })
            .then(() => {
                return this._modalUserFeedback.displayCustom(
                    new ScheduleVeilComponent(this._logger, false, titleMsg, {
                        workType: "plan",
                        firstTime: true,
                    }),
                    {
                        dontHide: false,
                    }
                );
            })
            .then((scheduleDef) => {
                let schedule = scheduleDef.schedule;
                let description = workInput.description;
                let workInputObj = scheduleDef.workInput;
                if (entity) {
                    entity.createPlanSchedule(description, "", schedule, workInputObj);
                }
                return Promise.resolve();
            })
            .then(() => this._modalUserFeedback.hideDisplayed())
            .catch((err) => {
                if (err.notValidated) {
                    const errMessage = `This plan requires validation before it can be run.`;
                    this._modalUserFeedback.displayError({
                        error: {
                            errorMsg: errMessage,
                        },
                    });
                } else if (!err.userRejected) {
                    if (this._logger) {
                        this._logger.warn(
                            {
                                error: err,
                            },
                            "RunScreenComponent._schedulePlanWork() - Error"
                        );
                    } else {
                        console.error(err);
                    }
                    this._modalUserFeedback.displayError(err);
                }
            });
    }

    startPlan(/*, workInput*/) {
        /**
         *
         * @param {Object} progress - progress data object
         * @param {number} progress.startTime
         * @param {number} progress.targetCount
         * @param {number} progress.successCount
         * @param {number} progress.failureCount
         * @param {string} progress.planId
         * @param {string} progress.planName
         * @param {string} progress.planDescription
         */
        let workTarget = this._getWorkTarget();
        return this._checkDirty()
            .then(() => {
                return this._checkPlanValidated();
            })
            .then(() => {
                if (this._modalUserFeedback) {
                    this._modalUserFeedback.displayLoading({
                        message: "Running your plan... This could take a moment",
                    });
                }
            })
            .then(() => {
                return this._osPlanEntity.runPlansWork(
                    this._userSession.activeOrganization.id,
                    workTarget
                );
            })
            .then((result) => {
                let userNotificationMessage = "";
                if (!result.summary) {
                    userNotificationMessage = `Total number of devices: ${
                        result._trimmed.targetResults || 0
                    }`;
                } else {
                    const {targetCount, successCount, failureCount} = result.summary;
                    userNotificationMessage = [
                        `Total devices: ${targetCount || 0}`,
                        `Successful: ${successCount || 0}`,
                        `Errors: ${failureCount || 0}`,
                        "",
                    ].join("\r\n");
                }

                return this._modalUserFeedback
                    .displayConfirmation(userNotificationMessage, {
                        title: "Run Completed",
                        confirmDetails: {
                            text: "View Results",
                            type: "additive",
                        },
                        rejectDetails: {
                            text: "OK",
                            type: "text",
                        },
                    })
                    .then(() => {
                        if (result.targets && !_.isEmpty(result.targets)) {
                            return this._gameEditor.setActiveView(
                                Registrations.OS_PLAN_EDITOR_VIEW_RESULTS
                            );
                        } else {
                            this._logger.warn(
                                {error: {}},
                                "RunScreenComponent._startRunWork() - No policyId received"
                            );
                            return Promise.reject();
                        }
                    });
            })
            .catch((err) => {
                if (err.notValidated) {
                    const errMessage = `This plan requires validation before it can be run.`;
                    this._modalUserFeedback.displayError({
                        error: {
                            errorMsg: errMessage,
                        },
                    });
                } else if (!err.userRejected) {
                    if (this._logger) {
                        this._logger.warn(
                            {error: err},
                            "RunScreenComponent._startRunWork() - Error"
                        );
                    } else {
                        console.error(err);
                    }

                    this._modalUserFeedback.displayError(err);
                }
            });
    }

    _checkDirty() {
        if (this._gameEditor.getPlan().isDirty()) {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    return this._gameEditor.getPlan().update();
                })
                .catch(() => {
                    return Promise.resolve();
                });
        } else {
            return Promise.resolve();
        }
    }

    cancelRun() {
        this._context.reject({userRejected: true});
    }
    /**
     * Create workTarget Object from devices component selection
     * @param {String} selection
     * @returns {Object}
     */
    _getWorkTarget() {
        let workTarget = {
            category: "entity",
            entityType: "OsUpgradePlan",
            method: "single",
            id: this._gameEditor.getPlan().getId(),
        };
        return workTarget;
    }

    _checkPlanValidated() {
        if (this._gameEditor.getPlan().getStatus() === "VALIDATED") {
            return Promise.resolve();
        } else {
            return Promise.reject({
                notValidated: true,
                status: this._gameEditor.getPlan().getStatus(),
            });
        }
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = RunScreenComponent;
